import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import {
  ArrowLeft, RefreshCw, Loader2, UserPlus, Shield, Trash2, Mail,
  Send, Copy, X, AlertTriangle, CheckCircle2, Clock, KeyRound, ArrowUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { isScrollTopEnabled, setScrollTopEnabled } from '../../components/ScrollToTop';

const ROLE_LABELS = {
  owner: { label: 'Owner', color: '#FF2E00', bg: '#FFF1ED', border: '#FFBFAB' },
  admin: { label: 'Admin', color: '#0F0F0F', bg: '#F5F5F5', border: '#D1D5DB' },
  manager: { label: 'Manager', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  staff: { label: 'Staff', color: '#374151', bg: '#F9FAFB', border: '#E5E7EB' },
};

export default function TeamManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'staff' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get('/staff/users'),
        api.get('/staff/invites'),
      ]);
      setUsers(u.data || []);
      setInvites(i.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.name) { toast.error('Email and name required'); return; }
    setInviting(true);
    try {
      const r = await api.post('/staff/invites', form);
      const inviteUrl = r.data?.invite_url;
      if (inviteUrl) {
        navigator.clipboard?.writeText(inviteUrl).catch(() => {});
        toast.success(r.data?.email_sent ? `Invite emailed to ${form.email} · URL copied` : `Invite URL copied (email not sent — paste it to ${form.email})`);
      } else {
        toast.success('Invite created');
      }
      setForm({ email: '', name: '', role: 'staff' });
      setShowInviteForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally { setInviting(false); }
  };

  const copyInvite = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success('Invite URL copied');
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    try {
      await api.delete(`/staff/invites/${id}`);
      toast.success('Invite revoked');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateUser = async (userId, patch) => {
    try {
      await api.put(`/staff/users/${userId}`, patch);
      toast.success('Updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const removeUser = async (u) => {
    if (u.id === me?.id) { toast.error("You can't remove yourself"); return; }
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the team? They'll lose access immediately.`)) return;
    try {
      await api.delete(`/staff/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const sendReset = async (u) => {
    try {
      await api.post(`/staff/users/${u.id}/send-reset`);
      toast.success(`Password reset link sent to ${u.email}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const canInviteAdmin = me?.role === 'owner';

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="border-b border-gray-200 sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/staff/huscleos" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="team-back">
              <ArrowLeft size={16} /> Pipeline
            </Link>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-500 ml-2">TEAM</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="p-2 text-gray-500 hover:text-gray-900" data-testid="team-refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => setShowInviteForm(true)} className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-2 bg-[#FF2E00] text-white hover:bg-[#E02900]" data-testid="team-invite-btn">
              <UserPlus size={14} /> Invite teammate
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Invite form (inline drawer) */}
        {showInviteForm && (
          <div className="mb-6 p-6 bg-white border border-gray-200 shadow-sm" data-testid="team-invite-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900">Invite a teammate</h3>
              <button onClick={() => setShowInviteForm(false)} className="text-gray-400 hover:text-gray-900" data-testid="team-invite-close"><X size={16} /></button>
            </div>
            <form onSubmit={sendInvite} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full name *"
                  className="p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:border-[#FF2E00] focus:outline-none transition-colors" data-testid="invite-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email *"
                  className="p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:border-[#FF2E00] focus:outline-none transition-colors" data-testid="invite-email" />
              </div>
              <div>
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">ROLE</p>
                <div className="flex flex-wrap gap-2">
                  {['staff', 'manager', 'admin'].map((r) => {
                    if (r === 'admin' && !canInviteAdmin) return null;
                    const rc = ROLE_LABELS[r];
                    const active = form.role === r;
                    return (
                      <button key={r} type="button" onClick={() => setForm((f) => ({ ...f, role: r }))}
                        className={`text-[11px] font-bold tracking-widest uppercase px-4 py-2 border transition-all ${active ? '' : 'opacity-60 hover:opacity-100'}`}
                        style={{ color: rc.color, background: active ? rc.bg : 'white', borderColor: active ? rc.border : '#E5E7EB' }}
                        data-testid={`invite-role-${r}`}>
                        {rc.label}
                      </button>
                    );
                  })}
                </div>
                {!canInviteAdmin && <p className="text-[10px] text-gray-400 mt-2">Only owners can invite admins.</p>}
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button type="submit" disabled={inviting} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50 inline-flex items-center gap-2" data-testid="invite-submit">
                  {inviting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send invite
                </button>
                <button type="button" onClick={() => setShowInviteForm(false)} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed pt-2 border-t border-gray-100">
                The invite link is auto-copied to your clipboard. If our Resend email service is configured, the invitee also gets a branded HuscleOS email. Links expire in 7 days.
              </p>
            </form>
          </div>
        )}

        {/* Pending invites */}
        {invites.length > 0 && (
          <section className="mb-8" data-testid="team-invites-section">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} className="text-gray-500" />
              <h2 className="text-[10px] font-bold tracking-widest uppercase text-gray-500">PENDING INVITES ({invites.length})</h2>
            </div>
            <div className="space-y-2">
              {invites.map((inv) => {
                const expires = inv.expires_at ? new Date(inv.expires_at) : null;
                const expired = expires && expires < new Date();
                const rc = ROLE_LABELS[inv.role] || ROLE_LABELS.staff;
                const inviteUrl = `${window.location.origin}/staff/accept-invite?token=${inv.token}`;
                return (
                  <div key={inv.id} className="flex items-center gap-3 p-4 bg-white border border-gray-200" data-testid={`invite-row-${inv.id}`}>
                    <Mail size={16} className="text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold text-gray-900 truncate">{inv.name}</p>
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border" style={{ color: rc.color, background: rc.bg, borderColor: rc.border }}>{rc.label}</span>
                        {expired && <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">EXPIRED</span>}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{inv.email} · invited by {inv.created_by_name} · expires {expires ? expires.toLocaleDateString() : '—'}</p>
                    </div>
                    <button onClick={() => copyInvite(inviteUrl)} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 hover:border-gray-900 text-gray-700" data-testid={`invite-copy-${inv.id}`}>
                      <Copy size={11} className="inline mr-1" />Copy URL
                    </button>
                    <button onClick={() => revokeInvite(inv.id)} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-red-200 hover:bg-red-50 text-red-600" data-testid={`invite-revoke-${inv.id}`}>
                      <X size={11} className="inline mr-1" />Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Active team members */}
        <section data-testid="team-members-section">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-gray-500" />
            <h2 className="text-[10px] font-bold tracking-widest uppercase text-gray-500">ACTIVE TEAM ({users.length})</h2>
          </div>
          {loading && users.length === 0 ? (
            <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-20 bg-white border border-gray-200">
              <UserPlus size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-3">No team members yet.</p>
              <button onClick={() => setShowInviteForm(true)} className="text-sm font-bold text-[#FF2E00] hover:underline">Invite your first teammate</button>
            </div>
          ) : (
            <div className="space-y-2">
              {users.filter((u) => !u.is_demo).map((u) => {
                const rc = ROLE_LABELS[u.role] || ROLE_LABELS.staff;
                const isMe = u.id === me?.id;
                const canEdit = me?.role === 'owner' || (me?.role === 'admin' && u.role !== 'owner' && u.role !== 'admin');
                return (
                  <div key={u.id} className="flex items-center gap-3 p-4 bg-white border border-gray-200" data-testid={`user-row-${u.id}`}>
                    <div className="shrink-0 w-9 h-9 bg-gray-900 text-white flex items-center justify-center text-xs font-bold uppercase tracking-wider">
                      {(u.name || u.email || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-bold text-gray-900 truncate">{u.name}</p>
                        {isMe && <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">YOU</span>}
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border" style={{ color: rc.color, background: rc.bg, borderColor: rc.border }}>{rc.label}</span>
                        {!u.is_active && <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">DISABLED</span>}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{u.email} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</p>
                    </div>
                    {canEdit && !isMe && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <select
                          value={u.role}
                          onChange={(e) => updateUser(u.id, { role: e.target.value })}
                          className="text-[10px] font-bold tracking-widest uppercase px-2 py-1.5 border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-[#FF2E00] cursor-pointer"
                          data-testid={`user-role-${u.id}`}
                        >
                          <option value="staff">STAFF</option>
                          <option value="manager">MANAGER</option>
                          {me?.role === 'owner' && <option value="admin">ADMIN</option>}
                        </select>
                        <button onClick={() => sendReset(u)} title="Send password reset link" className="p-1.5 border border-gray-200 hover:border-gray-900 text-gray-600 hover:text-gray-900" data-testid={`user-reset-${u.id}`}><KeyRound size={12} /></button>
                        <button onClick={() => updateUser(u.id, { is_active: !u.is_active })} title={u.is_active ? 'Disable' : 'Enable'}
                          className={`p-1.5 border ${u.is_active ? 'border-gray-200 hover:border-amber-500 text-gray-600 hover:text-amber-600' : 'border-green-200 hover:bg-green-50 text-green-700'}`}
                          data-testid={`user-toggle-${u.id}`}>
                          {u.is_active ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
                        </button>
                        <button onClick={() => removeUser(u)} title="Remove from team" className="p-1.5 border border-red-200 hover:bg-red-50 text-red-600" data-testid={`user-remove-${u.id}`}><Trash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Personal preferences */}
        <section className="mt-10 pt-6 border-t border-gray-200" data-testid="team-prefs-section">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUp size={14} className="text-gray-500" />
            <h2 className="text-[10px] font-bold tracking-widest uppercase text-gray-500">YOUR PREFERENCES</h2>
          </div>
          <ScrollTopPreference />
        </section>
      </div>
    </div>
  );
}

function ScrollTopPreference() {
  const [on, setOn] = useState(isScrollTopEnabled());
  const toggle = () => {
    const next = !on;
    setOn(next);
    setScrollTopEnabled(next);
    toast.success(next ? 'Scroll-to-top button enabled' : 'Scroll-to-top button hidden');
  };
  return (
    <div className="flex items-center gap-4 p-4 bg-white border border-gray-200" data-testid="pref-scroll-top">
      <div className="shrink-0 w-9 h-9 bg-gray-900 text-white flex items-center justify-center">
        <ArrowUp size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900">Floating scroll-to-top button</p>
        <p className="text-xs text-gray-500">Shows in the bottom-right corner after you scroll 600px. Smooth-scrolls back to the top of any page.</p>
      </div>
      <button
        onClick={toggle}
        className={`relative inline-flex h-6 w-11 items-center transition-colors shrink-0 ${on ? 'bg-[#FF2E00]' : 'bg-gray-300'}`}
        data-testid="pref-scroll-top-toggle"
        aria-pressed={on}
      >
        <span className={`inline-block h-5 w-5 transform bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
      <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 w-8 text-center">{on ? 'ON' : 'OFF'}</span>
    </div>
  );
}
